import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button, openPlanWhatsapp } from "./BootcampLandingPage";
import { supabase } from "../../lib/supabase";
import { api } from "../../services/api";

/* ============================================================
   HARGA — samakan dengan info admin jika berubah
   ============================================================ */
const PLANS = [
  {
    id: "eb-sep",
    planId: "bootcamp_super_early_bird",
    name: "Super Early Bird",
    price: "7.999.000",
    bonus: "3 bulan",
    until: "8 Oktober 2026",
    payUrl: "",
    tag: "BEST VALUE",
    highlight: true,
    // Kedaluwarsa saat 9 Okt 2026 jam 00:00 WIB (= 8 Okt 17:00 UTC), lalu kartunya disembunyikan.
    expiresAt: "2026-10-08T17:00:00Z",
    hideWhenExpired: true,
  },
  {
    id: "eb-okt",
    planId: "bootcamp_early_bird_oktober",
    name: "Early Bird Oktober",
    price: "8.999.000",
    bonus: "2 bulan",
    until: "23 Oktober 2026",
    payUrl: "",
    tag: null as string | null,
    highlight: false,
    // Kedaluwarsa saat 24 Okt 2026 jam 00:00 WIB (= 23 Okt 17:00 UTC).
    // Kartu tetap tampil, hanya tombolnya yang mati.
    expiresAt: "2026-10-23T17:00:00Z" as string | null,
    hideWhenExpired: false,
  },
  {
    id: "reguler",
    planId: "bootcamp_reguler",
    name: "Reguler",
    price: "9.999.000",
    bonus: "2 bulan",
    until: "5 November 2026",
    payUrl: "",
    tag: null as string | null,
    highlight: false,
    expiresAt: null as string | null,
    hideWhenExpired: false,
  },
];

function isExpired(p: (typeof PLANS)[number]) {
  return !!p.expiresAt && Date.now() >= new Date(p.expiresAt).getTime();
}

const PAY_GROUPS: { title: string; items: string[] }[] = [
  { title: "QRIS", items: ["logo_qris.png"] },
  { title: "E-wallet", items: ["logo_gopay.png", "logo_spay.png", "logo_dana.png"] },
  { title: "Virtual Account", items: ["logo_bca.png", "logo_bni.png", "logo_pb.png"] },
  {
    title: "Kartu Kredit / Debit",
    items: ["logo_visa.png", "logo_mastercard.png", "logo_jcb.png", "logo_amex.png"],
  },
];

function payName(f: string) {
  return f.replace("logo_", "").replace(".png", "").toUpperCase();
}

const INFO = [
  {
    q: "Bagaimana cara mendaftar?",
    a: "Pilih paket di atas, klik Subscribe Now, login ke akun BandarmoloNY kamu, lalu selesaikan pembayaran di popup (QRIS, e-wallet, VA, atau kartu). Belum punya akun? Daftar dulu — gratis.",
  },
  {
    q: "Metode pembayaran apa saja?",
    a: "QRIS, E-wallet (GoPay, ShopeePay, DANA), Virtual Account bank, dan kartu kredit / debit via payment gateway.",
  },
  {
    q: "Kapan bootcamp dimulai?",
    a: "Batch 7 November 2026, live via Zoom setiap Sabtu & Senin–Kamis. Lihat jadwal lengkap di atas.",
  },
];

export function BootcampPricingSection() {
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  // Paket kedaluwarsa dengan hideWhenExpired disembunyikan; yang lain tetap tampil tapi nonaktif.
  // Highlight putih otomatis pindah ke paket pertama yang masih buka.
  const plans = PLANS.filter((p) => !(p.hideWhenExpired && isExpired(p)));
  const highlightId = plans.find((p) => !isExpired(p))?.id;

  const handleSubscribe = async (p: (typeof plans)[number]) => {
    if (loadingPlan || isExpired(p)) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      sessionStorage.setItem("returnTo", "/bootcamp");
      toast.info("Silakan login terlebih dahulu untuk melanjutkan pembayaran.");
      navigate("/auth?mode=login");
      return;
    }

    if (!(window as any).snap) {
      toast.error("Gateway pembayaran belum siap. Silakan refresh halaman.");
      openPlanWhatsapp(p.name, p.price);
      return;
    }

    setLoadingPlan(p.id);
    try {
      const response = await api.createSubscriptionOrder({
        planId: p.planId,
        paymentMethod: "snap",
      });

      if (!response.success) {
        toast.error(response.error || "Gagal membuat order pembayaran");
        return;
      }

      (window as any).snap.pay(response.data.snapToken, {
        onSuccess: () => {
          toast.success("Pembayaran berhasil! Akses bootcamp & platform Anda telah diaktifkan.");
        },
        onPending: () => {
          toast.info("Pembayaran sedang diproses. Silakan tunggu konfirmasi.");
        },
        onError: () => {
          toast.error("Pembayaran gagal. Silakan coba lagi.");
        },
        onClose: () => {},
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Title */}
        <div className="text-center max-w-2xl mx-auto mb-8">
          <p className="text-[0.6875rem] font-bold tracking-[0.25em] text-[#C9A45C] mb-2">PRICING</p>
          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-2">
            Pilih Paket Bootcampmu
          </h1>
          <p className="text-sm text-white/60">
            Akses full bootcamp + bonus. Pilih yang paling pas.
          </p>
        </div>

        {/* Tier cards */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 sm:p-4 mb-4">
          <div className={`grid gap-4 ${plans.length === 2 ? "md:grid-cols-2 max-w-4xl mx-auto" : "md:grid-cols-3"}`}>
            {plans.map((p) => {
              const highlighted = p.id === highlightId;
              const expired = isExpired(p);
              return (
              <div
                key={p.id}
                aria-disabled={expired || undefined}
                className={`relative flex flex-col rounded-3xl border p-5 overflow-hidden transition-all duration-300 ${
                  expired ? "opacity-50 grayscale" : "hover:-translate-y-1"
                } ${
                  highlighted
                    ? "border-[#C9A45C]/60 bg-white/[0.04]"
                    : "border-white/10 bg-transparent"
                }`}
              >
                {p.tag && (
                  <div className="absolute right-[-2.5rem] top-[1.25rem] w-[9.375rem] rotate-45 bg-[#C9A45C] py-1 text-center text-[0.6875rem] font-extrabold tracking-wider text-[#071A33] shadow z-10">
                    {p.tag}
                  </div>
                )}
                <h2 className="mb-1 text-center text-base font-semibold">{p.name}</h2>
                <div className="mb-3 text-center">
                  <span className="text-2xl font-extrabold">Rp {p.price}</span>
                  <div className="mx-auto mt-2.5 w-fit overflow-hidden rounded-md border border-[#408dbd] shadow-md">
                    <div className="bg-gradient-to-b from-[#358bc2] to-[#125887] px-5 py-1.5 text-center">
                      <p className="text-xs font-black uppercase tracking-wide text-[#fde047]">
                        FREE {p.bonus.toUpperCase()}
                      </p>
                      <p className="text-[0.625rem] font-bold uppercase tracking-wider text-white">
                        Bandarmolony AI
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-center text-[0.6875rem] text-white/50 mt-2">Pendaftaran s/d {p.until}</p>
                <div className="flex-1" />
                {expired ? (
                  <button
                    type="button"
                    disabled
                    className="mt-auto inline-flex w-full items-center justify-center rounded-full border-2 border-white/10 bg-transparent py-2 text-xs font-semibold text-white/40 cursor-not-allowed"
                  >
                    Pendaftaran Ditutup
                  </button>
                ) : (
                <Button
                  className={`mt-auto w-full py-2 text-xs font-semibold ${
                    highlighted
                      ? "bg-white text-[#071A33] hover:bg-white/90 shadow-md"
                      : "bg-transparent border-2 border-white/15 text-white/85 hover:bg-white/5"
                  }`}
                  onClick={() =>
                    p.payUrl
                      ? window.open(p.payUrl, "_blank", "noopener,noreferrer")
                      : handleSubscribe(p)
                  }
                >
                  {loadingPlan === p.id ? "Memproses..." : "Subscribe Now"}
                </Button>
                )}
              </div>
              );
            })}
          </div>
        </div>

        {/* Payment methods */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 mb-4">
          <h2 className="text-sm font-bold mb-4">Payment Methods</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {PAY_GROUPS.map((g) => (
              <div key={g.title} className="rounded-xl border border-white/40 p-2.5">
                <p className="text-[0.6875rem] font-bold text-white/50 text-center mb-1.5">{g.title}</p>
                <div className="flex gap-2">
                  {g.items.map((logoFile) => (
                    <span
                      key={logoFile}
                      className="flex h-16 w-16 items-center justify-center rounded-lg bg-white px-1 shadow-sm"
                    >
                      <img
                        src={`/bootcamp-lp/pay/${logoFile}?v=3`}
                        alt={payName(logoFile)}
                        className="max-h-12 max-w-14 object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fb = e.currentTarget.nextElementSibling as HTMLElement | null;
                          if (fb) fb.style.display = "inline";
                        }}
                      />
                      <span className="hidden text-[0.625rem] font-extrabold text-slate-700">
                        {payName(logoFile)}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[0.6875rem] text-white/40 mt-3">Pembayaran aman via payment gateway.</p>
        </div>

        {/* Additional info */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
          <h2 className="text-sm font-bold mb-3">Additional Information</h2>
          <div className="grid sm:grid-cols-3 gap-2.5">
            {INFO.map((it) => (
              <div key={it.q} className="rounded-xl bg-white/[0.03] border border-white/10 p-3.5">
                <h3 className="font-bold text-xs mb-1">{it.q}</h3>
                <p className="text-[0.6875rem] text-white/60 leading-relaxed">{it.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
  );
}
